import { Box, Typography, Divider, Paper, Grid } from '@mui/material';
import ListTitleBox from '../../../app/components/list/ListTitleBox';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../app/store/configureStore';
import { CartOrderSummary } from './CartOrderSummary';
import { CartItemList } from './CartItemList';
import { CartNotes } from './CartNotes';
import { CartItem } from '../../../app/data/models/cart';
import {
  createOrderAsync,
  updateProductQuantity,
  fetchRestaurantAndSetCart,
} from './cartSlice';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import OrderTypeSelector from './OrderTypeSelector';
import { OrderType } from '../../../app/data/models/orders/orderType';
import { isValidSwissPhoneNumber } from '../../../app/utils/phoneNumberValidator';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import LoadingComponent from '../../../app/layout/LoadingComponent';

const RESTAURANT_TABLE_KEY = (restaurantId: string) =>
  `restaurant_${restaurantId}_table_number`;

const CartPage = () => {
  const { t } = useTranslation();
  const { cart, status, restaurant } = useAppSelector((state) => state.cart);
  const { status: restaurantStatus } = useAppSelector(
    (state) => state.customerRestaurants
  );
  const restaurantId = cart.restaurantId;

  const [tableNumber, setTableNumber] = useState<number | undefined>(() => {
    if (!restaurantId) return undefined;
    // Get table number from restaurant-specific storage
    const savedTableNumber = sessionStorage.getItem(
      RESTAURANT_TABLE_KEY(restaurantId)
    );
    return savedTableNumber ? Number(savedTableNumber) : undefined;
  });

  const [tableNumberError, setTableNumberError] = useState<string | null>(null);

  const handleTableNumberChange = (newTableNumber: number | undefined) => {
    if (!restaurantId) return;

    setTableNumber(newTableNumber);
    if (newTableNumber) {
      sessionStorage.setItem(
        RESTAURANT_TABLE_KEY(restaurantId),
        newTableNumber.toString()
      );
    } else {
      sessionStorage.removeItem(RESTAURANT_TABLE_KEY(restaurantId));
    }
  };

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [orderType, setOrderType] = useState<OrderType>(OrderType.DineIn);
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [phoneNumberError, setPhoneNumberError] = useState<string | null>(null);
  const [customerNameError, setCustomerNameError] = useState<string | null>(
    null
  );
  const [notes, setNotes] = useState<string>('');

  useEffect(() => {
    if (restaurantId) {
      dispatch(fetchRestaurantAndSetCart({ restaurantId }));
    }
  }, [dispatch, restaurantId]);

  const handleQuantityChange = (cartItem: CartItem, quantity: number) => {
    dispatch(
      updateProductQuantity({
        cartItem: { ...cartItem },
        quantity,
        restaurantId,
      })
    );
  };

  const validateForm = (): boolean => {
    if (orderType === OrderType.TakeAway) {
      if (!customerName.trim()) {
        setCustomerNameError(t('cart.errors.name-required'));
        return false;
      }
      if (!customerPhone) {
        setPhoneNumberError(t('cart.errors.phone-required'));
        return false;
      }
      if (!isValidSwissPhoneNumber(customerPhone)) {
        setPhoneNumberError(t('cart.errors.phone-invalid'));
        return false;
      }
    } else if (orderType === OrderType.DineIn) {
      if (!tableNumber) {
        setTableNumberError(t('cart.errors.table-number-required'));
        return false;
      }
    }
    return true;
  };

  const handleCheckout = async () => {
    if (!validateForm()) return;

    try {
      const order = await dispatch(
        createOrderAsync({
          cart: {
            restaurantId: cart.restaurantId,
            restaurantName: cart.restaurantName,
            orderItems: cart.orderItems,
            orderType,
            tableNumber:
              orderType === OrderType.DineIn ? tableNumber : undefined,
            customerName:
              orderType === OrderType.TakeAway ? customerName : undefined,
            customerPhone:
              orderType === OrderType.TakeAway ? customerPhone : undefined,
            notes: notes.trim() || undefined,
            totalPrice: cart.totalPrice,
            totalQuantity: cart.totalQuantity,
          },
        })
      ).unwrap();

      if (!order) return;
      navigate(`/customer/orders/${order.id}`);
    } catch (error) {
      console.error('Failed to create order:', error);
      toast.error(t('cart.errors.create-order'));
    }
  };

  const handleBackToRestaurant = () => {
    if (cart.restaurantId) {
      navigate(`/customer/restaurants/${cart.restaurantId}`);
    } else {
      navigate(-1); // Fallback to browser back if no restaurant ID is available
    }
  };

  if (restaurantStatus.includes('pending')) return <LoadingComponent />;

  if (cart.orderItems.length === 0) {
    return (
      <Box>
        <ListTitleBox
          title={<Typography variant="h4">{t('cart.title')}</Typography>}
          onBackClick={handleBackToRestaurant}
          paddingBottom={0.5}
        />
        <Paper
          elevation={3}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '50vh',
          }}
        >
          <Typography variant="h5" component="div" gutterBottom>
            {t('cart.no-items')}
          </Typography>
          <Typography color="text.secondary">
            {t('cart.add-items-prompt')}
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box>
        <ListTitleBox
          title={<Typography variant="h4">{t('cart.title')}</Typography>}
          onBackClick={handleBackToRestaurant}
          paddingBottom={0.5}
        />

        <Typography variant="subtitle1" color="text.secondary">
          {cart.restaurantName}
        </Typography>
        <Divider sx={{ mb: 2, mt: 1 }} />
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <CartItemList
            cartItems={cart.orderItems}
            defaultTaxRate={cart.defaultTaxRate}
            onQuantityChange={handleQuantityChange}
          />
          <CartNotes notes={notes} onNotesChange={setNotes} />
        </Grid>
        <Grid item xs={12} md={4}>
          <OrderTypeSelector
            orderType={orderType}
            tableNumber={tableNumber}
            tableNumberError={tableNumberError}
            customerName={customerName}
            customerPhone={customerPhone}
            onOrderTypeChange={setOrderType}
            onTableNumberChange={handleTableNumberChange}
            onCustomerNameChange={setCustomerName}
            onCustomerPhoneChange={(value) => {
              setCustomerPhone(value);
              setPhoneNumberError(null);
            }}
            phoneNumberError={phoneNumberError}
            customerNameError={customerNameError}
          />
          <CartOrderSummary
            cart={cart}
            status={status}
            tableNumber={tableNumber}
            orderType={orderType}
            onCheckout={handleCheckout}
            priceIncludesTax={restaurant?.settings?.priceIncludesTax ?? false}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default CartPage;
